"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import { getPopularLink404Desktop, getPopularLink404Mobile } from "../../api/common";
import { CANT_FIND_PAGE, PAGE_NOT_FOUND, TRY_POPULAR_PAGES } from "../../constants/common/common";

const Chip = dynamic(() => import("../../components/atoms/chip"));
const Typography = dynamic(() => import("../../components/atoms/typography"));
const CustomImage = dynamic(() => import("../../components/atoms/customImage"));

const imageDomain = process.env.NEXT_PUBLIC_ASSET_PREFIX;

/**
 * Client component that fetches and renders 404 page links
 * Only runs when page is actually rendered in browser (not during Next.js internal operations)
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isMobile - Whether the request is from mobile device
 * @returns {JSX.Element|null} The rendered component or null if loading/no data
 */
const NotFoundLinks = ({ isMobile }) => {
  const [link, setLink] = useState({});

  /**
   * Fetches 404 page data only when component mounts (page is actually rendered)
   */
  useEffect(() => {
    /**
     * Fetches 404 page data only when component mounts (page is actually rendered)
     */
    const fetch404Data = async () => {
      try {
        if (isMobile) {
          const mobileLink = await getPopularLink404Mobile();
          setLink(mobileLink || {});
        } else {
          const desktopLink = await getPopularLink404Desktop();
          setLink(desktopLink || {});
        }
      } catch (error) {
        console.log("[NOT-FOUND] Error fetching 404 data", error);
      }
    };

    fetch404Data();
  }, [isMobile]);

  /**
   * Renders a chip component for popular links
   *
   * @param {Object} item - Link item data
   * @param {number} index - Item index
   * @returns {JSX.Element} The rendered chip component
   */
  const renderChip = (item, index) => (
    <Link href={item?.url} key={index} className="cursor-pointer">
      <Chip
        id={`popular-links-chip-${index}`}
        content={item?.text}
        variant="outlined"
        size="medium"
        startIcon={item?.icon}
        iconSize={20}
        color="dark"
        classes="text-sm hover:border-1 hover:border-darkOliveGreen hover:bg-fnp-50 hover:text-fnp-400"
      />
    </Link>
  );

  return (
    <div className="justify-content flex h-screen flex-col justify-center md:pt-100 lg:pt-100">
      <CustomImage
        src={`${process.env.NEXT_PUBLIC_ASSET_PREFIX}/assets/images/custom/service500.gif`}
        alt="Page Not Found"
        width={isMobile ? 328 : 350}
        height={isMobile ? 140 : 200}
        className="mx-auto"
      />
      <div className="flex flex-col">
        <h2 className="leading-28 mb-8 text-center text-16 font-600 leading-22 text-fnp-500 md:text-20">
          {PAGE_NOT_FOUND}
        </h2>
        <Typography Tag="p" classes="text-14 leading-18 font-400 text-fnp-500 text-center md:text-16 md:leading-20">
          {CANT_FIND_PAGE}
          <br />
          {TRY_POPULAR_PAGES}
        </Typography>
      </div>
      {link && Object.keys(link).length > 0 && (
        <>
          <div className="my-20 flex flex-col justify-center gap-8">
            <div className="flex flex-row justify-center gap-8">
              {link?.chips1?.map((item, index) => renderChip(item, index))}
            </div>
            <div className="flex flex-row justify-center gap-8">
              {link?.chips2?.map((item, index) => renderChip(item, index))}
            </div>
          </div>
          {!isMobile && link?.downloadApp && (
            <div className="mx-auto flex flex-col justify-center gap-8 pt-20 text-center">
              <Typography variant="body1" Tag="span" classes="text-16 font-bold">
                {link?.downloadApp?.text}
              </Typography>
              <div className="flex flex-row justify-center gap-8">
                {link?.downloadApp?.blocks?.map((item, index) => (
                  <Link href={item?.url} key={index}>
                    <CustomImage
                      src={imageDomain + item?.imageSrc}
                      alt="Page Not Found"
                      width={137}
                      height={43}
                      className="mx-auto rounded-lg"
                    />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

NotFoundLinks.propTypes = {
  isMobile: PropTypes.bool.isRequired,
};

export default NotFoundLinks;
